import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "notistack";
import { IoClose } from "react-icons/io5";
import MaInput from "../../../../Components/UI/ma-input";
import MaButton from "../../../../Components/UI/ma-button";
import { formatWalletAmount } from "./payouts-mock-data";
import boxImg from "../../../../assets/images/box.png";
import { MdLightbulb } from "react-icons/md";
import { PiBankFill } from "react-icons/pi";
import { GET_MY_BANKS } from "../../../../Utils/Queries";
import { WITHDRAWAL_REQUEST } from "../../../../Utils/Mutations";

function parseAmountToCents(raw) {
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function WithdrawModal({
  open,
  onClose,
  availableCents,
  onRequestAddAccount,
  onWithdraw,
}) {
  const [amount, setAmount] = useState("");
  const [selectedBankId, setSelectedBankId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const { data: banksData, loading: banksLoading, refetch: refetchBanks } =
    useQuery(GET_MY_BANKS, {
      fetchPolicy: "cache-and-network",
    });
  const allBanks = useMemo(
    () => banksData?.allMyBanks ?? [],
    [banksData?.allMyBanks],
  );
  const hasSettlementAccount = allBanks.length > 0;
  const selectedBank = useMemo(
    () =>
      allBanks.find((b) => b?._id === selectedBankId) ?? allBanks[0] ?? null,
    [allBanks, selectedBankId],
  );

  const [withdrawRequest, { loading: withdrawLoading }] =
    useMutation(WITHDRAWAL_REQUEST);

  useEffect(() => {
    if (open) {
      setAmount("");
      setSelectedBankId(null);
      refetchBanks().catch(() => {});
    }
  }, [open, refetchBanks]);

  useEffect(() => {
    if (!selectedBankId && allBanks.length > 0) {
      setSelectedBankId(allBanks[0]?._id ?? null);
    }
  }, [allBanks, selectedBankId]);

  const amountCents = parseAmountToCents(amount);
  const overMax =
    amountCents != null &&
    Number.isFinite(availableCents) &&
    amountCents > availableCents;
  const validAmount = amountCents != null && amountCents > 0 && !overMax;

  const canWithdraw =
    hasSettlementAccount && !!selectedBank?._id && validAmount && !withdrawLoading;

  const handleWithdraw = async () => {
    if (!canWithdraw || !selectedBank?._id) return;
    try {
      await withdrawRequest({
        variables: {
          bankId: selectedBank._id,
          amount: parseFloat(amount),
        },
      });
      onWithdraw?.(amountCents);
      setAmount("");
      onClose?.();
    } catch (err) {
      enqueueSnackbar(err?.message ?? "Couldn’t submit withdrawal.", {
        variant: "error",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={withdrawLoading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          maxWidth: 440,
          width: "100%",
        },
      }}
    >
      <DialogTitle className="flex items-center justify-between pr-2">
        <span className="text-[16px] font-semibold text-[#18181B]">
          Withdraw Balance
        </span>
        <IconButton
          sx={{
            bgcolor: "#F7F7F5",
            borderRadius: "8px",
            width: "32px",
            height: "32px",
          }}
          size="small"
          onClick={onClose}
          aria-label="Close"
          disabled={withdrawLoading}
        >
          <IoClose className="text-xl text-[#DC2626]" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="space-y-4 pt-2">
        <div className="rounded-[10px] flex items-center justify-between border border-[#E5E4E2] border-solid bg-[#F7F7F5] px-4 py-3">
          <div>
            <p className="my-0 text-[10px] font-bold uppercase text-[#9B9895]">
              Available Balance
            </p>
            <p className="my-0 mt-1 text-[20px] font-black tabular-nums text-[#111827]">
              {formatWalletAmount(availableCents)}
            </p>
          </div>
          <div>
            <img src={boxImg} width={50} />
          </div>
        </div>

        <MaInput
          label="Amount"
          fullWidth
          placeholder="Enter Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          borderColor="#E5E7EB"
          disabled={withdrawLoading}
        />
        {overMax && (
          <p className="my-0 text-[12px] font-medium text-red-600">
            Amount exceeds available balance.
          </p>
        )}

        {hasSettlementAccount && (
          <div className="space-y-2">
            <p className="my-0 text-[12px] font-semibold text-[#272727]">
              Settlement Account
            </p>
            <div className="max-h-[180px] space-y-2 overflow-y-auto pr-1">
              {allBanks.map((bank) => {
                const last4 = String(bank?.accountNumber ?? "").slice(-4);
                const isSelected = bank?._id === selectedBank?._id;
                return (
                  <button
                    type="button"
                    key={bank?._id}
                    onClick={() => setSelectedBankId(bank?._id ?? null)}
                    disabled={withdrawLoading}
                    className={`flex w-full items-center gap-3 rounded-[12px] border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed ${
                      isSelected
                        ? "border-[#2E1834] bg-[#FAF5FD]"
                        : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]"
                    }`}
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        isSelected ? "bg-[#2E1834]" : "bg-[#F3F4F6]"
                      }`}
                    >
                      <PiBankFill
                        className={`text-[18px] ${
                          isSelected ? "text-white" : "text-[#6B7280]"
                        }`}
                      />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[13px] font-semibold text-[#111827]">
                        {bank?.bankName ?? "—"}
                      </span>
                      <span className="text-[11px] font-medium text-[#6B7280]">
                        ****{last4 || "----"}
                      </span>
                    </span>
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        isSelected
                          ? "border-[#2E1834] bg-[#2E1834]"
                          : "border-[#D1D5DB] bg-white"
                      }`}
                    >
                      {isSelected && (
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {banksLoading && allBanks.length === 0 ? (
          <p className="my-0 text-[12px] text-[#9CA3AF]">
            Loading settlement accounts…
          </p>
        ) : !hasSettlementAccount ? (
          <div className="rounded-[16px]  border-solid border-[#E5E7EB80] bg-[#FAF5FD] px-4 py-2 flex justify-between items-center">
            <div className="flex items-center gap-x-2">
              <MdLightbulb className="text-[#FFCC00]" />
              <p className="my-0 text-[11px] font-black text-[#3D3D3D]">
                Add Settlement Account
              </p>
            </div>
            <Button
              onClick={onRequestAddAccount}
              sx={{
                bgcolor: "#2E1834",
                px: 3,
                borderRadius: "12px",
                color: "#fff",
              }}
            >
              Add Account
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-[#E5E7EB80] border-solid bg-[#F9FAFB] px-4 py-3 flex items-center gap-x-3">
            <MdLightbulb className="text-[#FFCC00] text-[20px]" />
            <p className="my-0 text-[11px] leading-relaxed text-[#6B7280]">
              Service fee will be charge for cashout after last funding is more
              than 7 days.
            </p>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3 }} className="px-4 pb-10 pt-0 mb-5 ">
        <MaButton
          title="Cancel"
          onClick={onClose}
          disabled={withdrawLoading}
          sx={{
            bgcolor: "#fff",
            borderRadius: "12px",
            fontWeight: 600,
            border: "1px solid #EAEAEA80",
            color: "#DC2626",
          }}
        />
        <MaButton
          variant="primary"
          title={withdrawLoading ? "Processing…" : "Proceed"}
          disabled={!canWithdraw}
          loading={withdrawLoading}
          onClick={handleWithdraw}
          sx={{
            bgcolor: "#2E1834",
            borderRadius: "12px",
            fontWeight: 600,
          }}
        />
      </DialogActions>
    </Dialog>
  );
}
